import React, { useEffect } from "react";
import { ILoginData } from "../../types/authTypes";
import { useAlertHelpers } from "../../helper/Hooks/useAlertHelpers";
import { useSession } from "../../contexts/SessionContext";
import { useAuth } from "../../contexts/AuthContext";
import { LocationState } from "../../types/routeTypes";
import { useLocation, useNavigate } from "react-router";
import { useLoginState } from "../login/useLoginState";
import { patientLoginConfig } from "../login/loginConfigs";
import SharedLogin from "../login/sharedLogin";

interface LoginPatientProps {
  title: string;
}

/**
 * function component of logining patient
 * - use `useLoginState` hook to manage state
 * - use `SharedLogin` component to render UI
 * - include only patient-sepecific business logic
 */
const LoginPatient = ({ title }: LoginPatientProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;

  const { login: authLogin } = useAuth();
  const { login: sessionLogin } = useSession();
  const { reportNoClinicSelected, showAlertModal } = useAlertHelpers();

  const loginState = useLoginState(patientLoginConfig);

  /**
   * Patient initialization
   * - clear the session
   * - set the page title
   */
  useEffect(() => {
    sessionStorage.removeItem("auth");
    document.title = title;
  }, [title]);

  /**
   * Method for handle the login.
   * The input data are stringified to JSON.
   * Then send JSON to backend API and handle the response.
   */
  const handleLogin = async (event) => {
    event.preventDefault();
    if (!loginState.username) {
      console.warn("Kein Username");
      showAlertModal("Kein SysDok-ID angegeben.");
      return;
    }
    if (!loginState.password) {
      console.warn("Kein Passwort");
      showAlertModal("Kein Geburtsdatum angegeben.");
      return;
    }
    if (!loginState.clinicId) {
      console.warn("Keine Klinik");
      reportNoClinicSelected();
      return;
    }

    /* changing the date string iso date string*/
    const [day, month, year] = loginState.password.split(".");
    const isoDate: string = [year, month, day].join("-");

    const loginData: ILoginData = {
      clinicName: loginState.clinicname,
      clinicId: loginState.clinicId,
      username: loginState.username,
      password: isoDate,
      role: "patient",
      userType: "patient",
      clinicContact: loginState.clinic_contact,
    };

    if (typeof authLogin === "function") {
      await authLogin(loginData, (authResponse: string) => {
        if (authResponse === "success") {
          sessionLogin();
          if (
            locationState?.redirectTo &&
            locationState.redirectTo.length > 0
          ) {
            navigate(locationState.redirectTo);
          } else {
            navigate("/patients");
          }
        } else if (authResponse === "credentials_wrong") {
          showAlertModal(
            "Ihre Anmeldedaten sind fehlerhaft oder Ihr Account wurde noch nicht freigeschaltet.",
          );
        } else if (authResponse === "no_permissions") {
          showAlertModal("Sie haben für diese Seite keine Berechtigung.");
        } else {
          showAlertModal(
            "Irgendetwas ist schief gelaufen. Bitte versuchen Sie es erneut.",
          );
        }
      });
    }
  };

  return (
    <SharedLogin
      config={patientLoginConfig}
      clinics={loginState.clinics}
      onClinicIdChange={loginState.handleClinicId}
      onUsernameChange={loginState.handleUsername}
      onPasswordChange={loginState.handlePassword}
      onSubmit={handleLogin}
    />
  );
};

export default LoginPatient;
